import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  InputAdornment,
  IconButton,
  Alert,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../api/axios';
import { useDarkMode } from '../App';

function ChangePasswordPage({ username, onPasswordChanged }) {
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('error');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');

    const errors = {};
    if (!currentPassword) errors.currentPassword = '현재 비밀번호를 입력해 주세요';
    if (!newPassword) errors.newPassword = '새 비밀번호를 입력해 주세요';
    else if (newPassword.length < 8) errors.newPassword = '비밀번호는 최소 8자 이상이어야 합니다';
    if (!confirmPassword) errors.confirmPassword = '새 비밀번호를 한 번 더 입력해 주세요';
    else if (newPassword !== confirmPassword) errors.confirmPassword = '비밀번호가 일치하지 않습니다';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      const res = await api.post('/auth/change-initial-password', {
        username,
        currentPassword,
        newPassword,
        confirmPassword
      });
      
      // 성공 메시지 표시 (로딩 상태 유지)
      setMsg(res.data.message);
      setMsgType('success');
      
      // 로딩 상태 유지하면서 즉시 페이지 이동
      setTimeout(() => {
        onPasswordChanged?.(res.data.user);
        // 로그인 페이지 또는 대시보드로 이동
        navigate('/login');
      }, 500);
      
    } catch (err) {
      setMsg(err?.response?.data?.message || '비밀번호 변경 실패');
      setMsgType('error');
      setLoading(false); // 실패 시에만 로딩 해제
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: darkMode
          ? 'linear-gradient(180deg, #121212 0%, #1e1e1e 100%)'
          : 'linear-gradient(180deg, #fefefe 0%, #fff9f2 100%)',
        px: 2
      }}
    >
      {/* 로딩 오버레이 - 페이지 이동 전까지 유지 */}
      <Backdrop
        sx={{
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1300,
          backdropFilter: 'blur(4px)'
        }}
        open={loading}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CircularProgress 
            size={50} 
            sx={{ color: '#d4af37' }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#fff',
              fontWeight: 'bold',
              letterSpacing: '0.5px'
            }}
          >
            비밀번호 변경 중입니다...
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center'
            }}
          >
            잠시만 기다려주세요.
          </Typography>
        </Box>
      </Backdrop>

      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 6 },
          borderRadius: 3,
          width: { xs: '100%', sm: 500 },
          maxWidth: '100%',
          bgcolor: 'background.paper',
          boxShadow: darkMode
            ? '0px 8px 20px rgba(0,0,0,0.4)'
            : '0px 8px 20px rgba(0,0,0,0.1)',
          pointerEvents: loading ? 'none' : 'auto',
          opacity: loading ? 0.5 : 1,
          transition: 'all 0.3s ease'
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          mb={1}
          sx={{
            fontFamily: 'serif',
            color: 'text.primary',
            textAlign: 'center'
          }}
        >
          비밀번호 변경
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          mb={3} 
          sx={{ textAlign: 'center' }}
        >
          처음 로그인하셨습니다. 새로운 비밀번호를 설정해주세요.
        </Typography>

        {msg && (
          <Alert severity={msgType} sx={{ mb: 2 }}>
            {msg}
          </Alert>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              type={showCurrent ? "text" : "password"}
              label="현재 비밀번호"
              name="currentPassword"
              autoComplete="current-password"
              value={currentPassword}
              onChange={e => { setCurrentPassword(e.target.value); setFieldErrors(prev => ({ ...prev, currentPassword: undefined })); }}
              required
              disabled={loading}
              error={!!fieldErrors.currentPassword}
              helperText={fieldErrors.currentPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrent(prev => !prev)}
                      edge="end"
                      disabled={loading}
                    >
                      {showCurrent ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#d4af37',
                  },
                },
              }}
            />

            <TextField
              fullWidth
              type={showNew ? "text" : "password"}
              label="새 비밀번호"
              name="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setFieldErrors(prev => ({ ...prev, newPassword: undefined })); }}
              required
              disabled={loading}
              error={!!fieldErrors.newPassword}
              helperText={fieldErrors.newPassword || "최소 8자 이상"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNew(prev => !prev)}
                      edge="end"
                      disabled={loading}
                    >
                      {showNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#d4af37',
                  },
                },
              }}
            />

            <TextField
              fullWidth
              type={showConfirm ? "text" : "password"}
              label="새 비밀번호 확인"
              name="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setFieldErrors(prev => ({ ...prev, confirmPassword: undefined })); }}
              required
              disabled={loading}
              error={!!fieldErrors.confirmPassword || (confirmPassword !== '' && newPassword !== confirmPassword)}
              helperText={fieldErrors.confirmPassword || (confirmPassword !== '' && newPassword !== confirmPassword ? '비밀번호가 일치하지 않습니다' : '')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirm(prev => !prev)}
                      edge="end"
                      disabled={loading}
                    >
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#d4af37',
                  },
                },
              }}
            />

            <Button
              fullWidth
              type="submit"
              size="large"
              variant="contained"
              disabled={loading}
              sx={{
                background: 'linear-gradient(90deg, #222 0%, #555 100%)',
                color: '#f5e1a4',
                fontWeight: 'bold',
                textTransform: 'none',
                py: 1.8,
                borderRadius: 2,
                fontSize: '1rem',
                mt: 2,
                '&:hover': {
                  background: 'linear-gradient(90deg, #555 0%, #222 100%)',
                },
                '&:disabled': {
                  background: 'linear-gradient(90deg, #999 0%, #666 100%)',
                  color: '#ccc',
                  cursor: 'not-allowed'
                }
              }}
            >
              {loading ? "변경 중..." : "비밀번호 변경"}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}

export default ChangePasswordPage;